import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Icon from './AppIcon';
import { TERMS_VERSION } from '../constants/termsOfService';
import { LEGAL_DOCUMENTS, LEGAL_EFFECTIVE_DATE } from '../constants/legalDocuments';
import LegalDocumentContent from './legal/LegalDocumentContent';

const SECTION_META = {
  privacy: {
    icon: 'Shield',
    gradient: 'from-blue-500 to-cyan-500',
    chip: 'bg-blue-100 text-blue-700',
    ring: 'ring-blue-500/30',
  },
  terms: {
    icon: 'FileText',
    gradient: 'from-indigo-500 to-violet-500',
    chip: 'bg-indigo-100 text-indigo-700',
    ring: 'ring-indigo-500/30',
  },
  cookies: {
    icon: 'Cookie',
    gradient: 'from-amber-500 to-orange-500',
    chip: 'bg-amber-100 text-amber-800',
    ring: 'ring-amber-500/30',
  },
  dpa: {
    icon: 'Database',
    gradient: 'from-emerald-500 to-teal-500',
    chip: 'bg-emerald-100 text-emerald-700',
    ring: 'ring-emerald-500/30',
  },
};

const ACCEPT_BUTTON_CLASS =
  'px-5 py-2.5 rounded-xl text-sm font-semibold bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-45 disabled:cursor-not-allowed disabled:hover:bg-blue-600 flex items-center justify-center gap-2 shadow-sm transition-all duration-300';

const SignatureReveal = ({ name, animKey }) => {
  const displayName = name?.trim() || 'Authorised user';
  const chars = [...displayName];
  const charBaseDelay = 0.12;
  const signedDate = new Date().toLocaleDateString('en-GB', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  });
  const totalWriteDuration = chars.length * charBaseDelay + 0.55;

  return (
    <div
      key={animKey}
      className="terms-sig-card relative rounded-2xl border border-primary/10 bg-card/80 backdrop-blur-md overflow-hidden shadow-[0_8px_32px_-8px_rgba(37,99,235,0.18)]"
    >
      <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(37,99,235,0.08),transparent_55%),radial-gradient(ellipse_at_bottom_right,rgba(139,92,246,0.08),transparent_50%)] pointer-events-none" />
      <div className="terms-sig-shimmer pointer-events-none absolute inset-0 overflow-hidden" aria-hidden="true" />

      <div className="relative px-4 sm:px-5 pt-4 pb-3">
        <div className="flex items-center justify-between gap-3 mb-4">
          <div className="flex items-center gap-2">
            <span className="terms-sig-icon flex items-center justify-center w-8 h-8 rounded-xl bg-gradient-to-br from-primary/15 to-violet-500/15 text-primary ring-1 ring-primary/10">
              <Icon name="Sparkles" size={15} />
            </span>
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-muted-foreground">
                Signing as
              </p>
              <p className="text-xs font-medium text-foreground/80">Agreement confirmation</p>
            </div>
          </div>
          <span className="terms-sig-badge inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50/90 border border-emerald-200/70 px-2.5 py-1 rounded-full shadow-sm">
            <Icon name="Check" size={10} strokeWidth={3} />
            Confirmed
          </span>
        </div>

        <div className="relative min-h-[3.25rem] sm:min-h-[3.5rem] px-1">
          <div
            className="terms-sig-name flex flex-wrap items-end gap-0 leading-none text-[1.75rem] sm:text-[2rem] text-primary drop-shadow-[0_1px_8px_rgba(37,99,235,0.15)]"
            style={{ fontFamily: '"Great Vibes", "Segoe Script", "Brush Script MT", cursive' }}
            aria-label={`Signature preview for ${displayName}`}
          >
            {chars.map((char, index) => (
              <span
                key={`${animKey}-${index}-${char}`}
                className="terms-sig-char inline-block"
                style={{ animationDelay: `${0.2 + index * charBaseDelay}s` }}
              >
                {char === ' ' ? '\u00A0' : char}
              </span>
            ))}
          </div>

          <svg
            className="terms-sig-flourish absolute -bottom-1 left-0 w-full h-8 pointer-events-none overflow-visible"
            viewBox="0 0 320 32"
            fill="none"
            preserveAspectRatio="none"
            aria-hidden="true"
          >
            <defs>
              <linearGradient id={`sig-grad-${animKey}`} x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="rgb(37, 99, 235)" stopOpacity="0.2" />
                <stop offset="50%" stopColor="rgb(124, 58, 237)" stopOpacity="0.85" />
                <stop offset="100%" stopColor="rgb(16, 185, 129)" stopOpacity="0.5" />
              </linearGradient>
            </defs>
            <path
              className="terms-sig-flourish-path"
              d="M4 22 C 48 8, 72 28, 108 18 S 168 6, 204 20 S 268 26, 316 14"
              stroke={`url(#sig-grad-${animKey})`}
              strokeWidth="2.25"
              strokeLinecap="round"
              pathLength="1"
            />
          </svg>

          <div
            className="terms-sig-pen absolute bottom-1 flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary shadow-[0_0_12px_rgba(37,99,235,0.35)] ring-2 ring-primary/20 pointer-events-none"
            style={{ animationDuration: `${totalWriteDuration}s` }}
          >
            <Icon name="Pen" size={11} />
          </div>
        </div>

        <p
          className="terms-sig-meta mt-4 text-[11px] text-muted-foreground flex items-center gap-1.5"
          style={{ animationDelay: `${totalWriteDuration + 0.35}s` }}
        >
          <Icon name="Calendar" size={11} className="opacity-60" />
          {signedDate}
          <span className="text-border">·</span>
          Meal Manager &amp; Activity Planner
        </p>
      </div>

      <div className="terms-sig-disclaimer relative border-t border-border/50 bg-muted/25 px-4 py-2">
        <p className="text-[9px] leading-snug text-muted-foreground/75 text-center">
          Visual animation only — not a legal signature. Your digital acceptance is securely
          recorded when you click Accept.
        </p>
      </div>
    </div>
  );
};

const TermsAcceptanceModal = ({
  onAccept,
  onLogout,
  termsVersion = TERMS_VERSION,
  isSubmitting = false,
  error = '',
  defaultSignerName = '',
}) => {
  const scrollRef = useRef(null);
  const bottomSentinelRef = useRef(null);
  const sectionRefs = useRef({});
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeSectionId, setActiveSectionId] = useState(LEGAL_DOCUMENTS[0]?.id || 'privacy');
  const [hasReachedBottom, setHasReachedBottom] = useState(false);
  const [isAcknowledged, setIsAcknowledged] = useState(false);
  const [signatureAnimKey, setSignatureAnimKey] = useState(0);
  const signatureName = defaultSignerName || '';
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const id = requestAnimationFrame(() => setIsVisible(true));
    return () => cancelAnimationFrame(id);
  }, []);

  const updateScrollProgress = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;

    const scrollable = el.scrollHeight - el.clientHeight;
    if (scrollable <= 0) {
      setScrollProgress(100);
      setHasReachedBottom(true);
      return;
    }

    const progress = (el.scrollTop / scrollable) * 100;
    setScrollProgress(Math.min(100, progress));

    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 24) {
      setHasReachedBottom(true);
    }
  }, []);

  useEffect(() => {
    const root = scrollRef.current;
    const sentinel = bottomSentinelRef.current;
    if (!root || !sentinel || !isVisible) return;

    const bottomObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setHasReachedBottom(true);
      },
      { root, threshold: 0, rootMargin: '0px 0px 0px 0px' },
    );
    bottomObserver.observe(sentinel);

    const sectionObserver = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        const sectionId = visible[0]?.target?.dataset?.sectionId;
        if (sectionId) setActiveSectionId(sectionId);
      },
      { root, rootMargin: '-12% 0px -55% 0px', threshold: [0, 0.15, 0.4, 0.7] },
    );

    LEGAL_DOCUMENTS.forEach((doc) => {
      const sectionEl = sectionRefs.current[doc.id];
      if (sectionEl) sectionObserver.observe(sectionEl);
    });

    const resizeObserver = new ResizeObserver(() => updateScrollProgress());
    resizeObserver.observe(root);

    updateScrollProgress();

    return () => {
      bottomObserver.disconnect();
      sectionObserver.disconnect();
      resizeObserver.disconnect();
    };
  }, [isVisible, updateScrollProgress]);

  const canAccept = useMemo(
    () => hasReachedBottom && isAcknowledged && !isSubmitting,
    [hasReachedBottom, isAcknowledged, isSubmitting],
  );

  const handleAcknowledgeChange = (checked) => {
    setIsAcknowledged(checked);
    if (checked) setSignatureAnimKey((key) => key + 1);
  };

  const handleAccept = async () => {
    if (!canAccept) return;
    await onAccept({ signatureName: signatureName.trim() });
  };

  const activeIndex = LEGAL_DOCUMENTS.findIndex((doc) => doc.id === activeSectionId);

  const acceptHint = !hasReachedBottom
    ? 'Scroll to the bottom to enable acceptance'
    : !isAcknowledged
      ? 'Tick the agreement box to sign and continue'
      : '';

  return (
    <div
      className={`fixed inset-0 z-[1000] flex items-center justify-center p-3 sm:p-4 transition-all duration-300 ${
        isVisible ? 'bg-black/60 backdrop-blur-sm' : 'bg-black/0'
      }`}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Great+Vibes&display=swap');
        @keyframes termsScrollBounce {
          0%, 100% { transform: translateY(0); opacity: 1; }
          50% { transform: translateY(8px); opacity: 0.75; }
        }
        @keyframes termsScrollPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(37, 99, 235, 0.35); }
          50% { box-shadow: 0 0 0 10px rgba(37, 99, 235, 0); }
        }
        @keyframes termsSigCardIn {
          0% { opacity: 0; transform: translateY(16px) scale(0.96); filter: blur(4px); }
          100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
        }
        @keyframes termsSigChar {
          0% { opacity: 0; transform: translateY(14px) scale(0.85) rotate(-2deg); filter: blur(8px); }
          60% { opacity: 1; transform: translateY(-2px) scale(1.03) rotate(0.5deg); filter: blur(0); }
          100% { opacity: 1; transform: translateY(0) scale(1) rotate(0deg); filter: blur(0); }
        }
        @keyframes termsSigFlourish {
          0% { stroke-dashoffset: 1; opacity: 0.3; }
          100% { stroke-dashoffset: 0; opacity: 1; }
        }
        @keyframes termsSigPen {
          0% { left: -4px; opacity: 0; transform: translateY(0) scale(0.8); }
          6% { opacity: 1; transform: translateY(0) scale(1); }
          94% { opacity: 1; }
          100% { left: calc(100% - 22px); opacity: 0; transform: translateY(-2px) scale(0.9); }
        }
        @keyframes termsSigBadge {
          0% { opacity: 0; transform: scale(0.5) rotate(-8deg); }
          70% { transform: scale(1.08) rotate(2deg); }
          100% { opacity: 1; transform: scale(1) rotate(0deg); }
        }
        @keyframes termsSigIcon {
          0% { opacity: 0; transform: scale(0.6); }
          100% { opacity: 1; transform: scale(1); }
        }
        @keyframes termsSigMeta {
          0% { opacity: 0; transform: translateX(-8px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        @keyframes termsSigDisclaimer {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
        @keyframes termsSigShimmer {
          0% { transform: translateX(-150%) skewX(-12deg); opacity: 0; }
          15% { opacity: 1; }
          100% { transform: translateX(250%) skewX(-12deg); opacity: 0; }
        }
        .terms-scroll-hint-icon { animation: termsScrollBounce 1.4s ease-in-out infinite; }
        .terms-scroll-hint-pill { animation: termsScrollPulse 2s ease-in-out infinite; }
        .terms-sig-card { animation: termsSigCardIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) forwards; }
        .terms-sig-char { opacity: 0; animation: termsSigChar 0.5s cubic-bezier(0.34, 1.4, 0.64, 1) forwards; }
        .terms-sig-flourish-path {
          stroke-dasharray: 1;
          stroke-dashoffset: 1;
          animation: termsSigFlourish 1.4s cubic-bezier(0.22, 1, 0.36, 1) 0.35s forwards;
        }
        .terms-sig-pen {
          animation-name: termsSigPen;
          animation-timing-function: cubic-bezier(0.45, 0.05, 0.25, 1);
          animation-fill-mode: forwards;
        }
        .terms-sig-badge { opacity: 0; animation: termsSigBadge 0.55s cubic-bezier(0.34, 1.56, 0.64, 1) 1.1s forwards; }
        .terms-sig-icon { opacity: 0; animation: termsSigIcon 0.4s ease-out 0.1s forwards; }
        .terms-sig-meta { opacity: 0; animation: termsSigMeta 0.45s ease-out forwards; }
        .terms-sig-disclaimer { opacity: 0; animation: termsSigDisclaimer 0.5s ease-out 1.65s forwards; }
        .terms-sig-shimmer::after {
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 40%;
          height: 100%;
          background: linear-gradient(90deg, transparent, rgba(255,255,255,0.55), transparent);
          animation: termsSigShimmer 0.9s ease-in-out 1.45s forwards;
        }
      `}</style>

      <div
        className={`w-full max-w-4xl h-[92vh] flex flex-col bg-card border border-border rounded-2xl shadow-2xl overflow-hidden transition-all duration-300 ${
          isVisible ? 'opacity-100 translate-y-0 scale-100' : 'opacity-0 translate-y-3 scale-[0.98]'
        }`}
      >
        {/* Header */}
        <div className="shrink-0 px-5 sm:px-6 py-4 sm:py-5 border-b border-border bg-gradient-to-br from-primary/5 via-card to-violet-500/5">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0">
              <h2 className="text-xl sm:text-2xl font-bold text-foreground tracking-tight flex items-center gap-2">
                <Icon name="Scale" size={22} className="text-primary shrink-0" />
                Legal Agreements
              </h2>
              <p className="text-sm text-muted-foreground mt-1">
                Version {termsVersion} · Effective {LEGAL_EFFECTIVE_DATE}
              </p>
              <div className="flex flex-wrap items-center gap-2 mt-2.5">
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-primary/10 text-primary">
                  <Icon name="UtensilsCrossed" size={12} />
                  Meal Manager
                </span>
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-violet-100 text-violet-700">
                  <Icon name="CalendarDays" size={12} />
                  Activity Planner
                </span>
              </div>
            </div>
            <span className="px-3 py-1 text-xs font-semibold rounded-full bg-amber-100 text-amber-700 shrink-0">
              Required
            </span>
          </div>

          <div className="mt-4 flex items-center gap-1">
            {LEGAL_DOCUMENTS.map((doc, index) => {
              const meta = SECTION_META[doc.id] || SECTION_META.terms;
              const isActive = doc.id === activeSectionId;
              const isPast = index < activeIndex || hasReachedBottom;
              return (
                <div key={doc.id} className="flex items-center flex-1 min-w-0">
                  <div
                    className={`flex items-center gap-1.5 min-w-0 transition-all duration-300 ${
                      isActive ? 'opacity-100' : isPast ? 'opacity-70' : 'opacity-40'
                    }`}
                  >
                    <span
                      className={`shrink-0 flex items-center justify-center w-6 h-6 rounded-full text-[10px] font-bold transition-all duration-300 ${
                        isPast && !isActive
                          ? 'bg-emerald-500 text-white'
                          : isActive
                            ? `bg-gradient-to-br ${meta.gradient} text-white ring-2 ${meta.ring}`
                            : 'bg-muted text-muted-foreground'
                      }`}
                    >
                      {isPast && !isActive ? <Icon name="Check" size={12} /> : index + 1}
                    </span>
                    <span
                      className={`hidden sm:block truncate text-[11px] font-semibold ${
                        isActive ? 'text-foreground' : 'text-muted-foreground'
                      }`}
                    >
                      {doc.shortTitle}
                    </span>
                  </div>
                  {index < LEGAL_DOCUMENTS.length - 1 && (
                    <div
                      className={`mx-1 h-0.5 flex-1 rounded-full transition-colors duration-300 ${
                        index < activeIndex || hasReachedBottom ? 'bg-emerald-400/60' : 'bg-border'
                      }`}
                    />
                  )}
                </div>
              );
            })}
          </div>

          <div className="mt-3 h-1.5 rounded-full bg-muted overflow-hidden">
            <div
              className="h-full rounded-full bg-gradient-to-r from-blue-500 via-violet-500 to-emerald-500 transition-[width] duration-150 ease-out"
              style={{ width: `${scrollProgress}%` }}
            />
          </div>
          <p className="mt-1.5 text-[11px] text-muted-foreground text-right tabular-nums">
            {Math.round(scrollProgress)}% read
          </p>
        </div>

        {/* Scrollable content */}
        <div className="relative flex-1 min-h-0 overflow-hidden">
          <div
            ref={scrollRef}
            onScroll={updateScrollProgress}
            className="h-full overflow-y-auto overscroll-y-contain px-5 sm:px-8 py-6 bg-gradient-to-b from-card to-muted/15"
          >
            {LEGAL_DOCUMENTS.map((doc, index) => {
              const meta = SECTION_META[doc.id] || SECTION_META.terms;
              return (
                <section
                  key={doc.id}
                  data-section-id={doc.id}
                  ref={(el) => {
                    sectionRefs.current[doc.id] = el;
                  }}
                  className={index > 0 ? 'mt-12 pt-2' : ''}
                >
                  <div className="mb-5 rounded-2xl overflow-hidden border border-border/70 bg-card shadow-sm">
                    <div className={`h-1 bg-gradient-to-r ${meta.gradient}`} />
                    <div className="px-4 sm:px-5 py-4 flex items-center gap-3 sm:gap-4">
                      <div
                        className={`shrink-0 flex items-center justify-center w-10 h-10 sm:w-11 sm:h-11 rounded-xl bg-gradient-to-br ${meta.gradient} shadow-md`}
                      >
                        <Icon name={meta.icon} size={20} className="text-white" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">
                          Section {index + 1} of {LEGAL_DOCUMENTS.length}
                        </p>
                        <h3 className="text-base sm:text-lg font-bold text-foreground leading-tight">
                          {doc.title}
                        </h3>
                      </div>
                      <span
                        className={`hidden sm:inline-flex shrink-0 px-2.5 py-1 rounded-full text-xs font-semibold ${meta.chip}`}
                      >
                        {doc.effectiveDate}
                      </span>
                    </div>
                  </div>

                  <LegalDocumentContent document={doc} omitTitle />

                  {index < LEGAL_DOCUMENTS.length - 1 && (
                    <div className="mt-10 flex items-center gap-3" aria-hidden="true">
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
                      <div className="flex items-center justify-center w-8 h-8 rounded-full bg-muted/80 border border-border/60">
                        <Icon name="ChevronDown" size={14} className="text-muted-foreground" />
                      </div>
                      <div className="flex-1 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
                    </div>
                  )}
                </section>
              );
            })}

            <div ref={bottomSentinelRef} className="h-px w-full" aria-hidden="true" />

            {hasReachedBottom && (
              <div className="mt-8 mb-2 flex items-center justify-center gap-2 py-4 rounded-xl bg-emerald-50 border border-emerald-200/80">
                <Icon name="CheckCircle2" size={18} className="text-emerald-600" />
                <span className="text-sm font-semibold text-emerald-800">
                  You&apos;ve read all agreements — confirm below to continue
                </span>
              </div>
            )}
          </div>

          {!hasReachedBottom && (
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 bottom-0 z-10 flex flex-col items-center justify-end pb-4 pt-20 bg-gradient-to-t from-card from-40% via-card/90 to-transparent"
            >
              <div className="terms-scroll-hint-pill flex flex-col items-center gap-2 px-5 py-3 rounded-2xl bg-card/95 border border-primary/20 shadow-lg backdrop-blur-sm">
                <div className="terms-scroll-hint-icon flex flex-col items-center -space-y-2 text-primary">
                  <Icon name="ChevronDown" size={22} strokeWidth={2.5} />
                  <Icon name="ChevronDown" size={22} strokeWidth={2.5} className="opacity-60" />
                </div>
                <p className="text-sm font-bold text-foreground text-center">
                  Scroll to the bottom to accept
                </p>
                <p className="text-[11px] text-muted-foreground text-center max-w-[240px]">
                  Read all {LEGAL_DOCUMENTS.length} sections, then tick the agreement box
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer — always visible */}
        <div className="shrink-0 px-5 sm:px-6 py-4 border-t border-border bg-surface/90 backdrop-blur-sm space-y-3">
          <label
            className={`flex items-start gap-3 p-3 rounded-xl border transition-all duration-200 ${
              hasReachedBottom
                ? 'border-border bg-card hover:border-primary/30 cursor-pointer'
                : 'border-border/60 bg-muted/30 opacity-60 cursor-not-allowed'
            }`}
          >
            <input
              type="checkbox"
              checked={isAcknowledged}
              onChange={(e) => handleAcknowledgeChange(e.target.checked)}
              disabled={!hasReachedBottom}
              className="mt-0.5 w-4 h-4 rounded border-border text-primary focus:ring-primary/30 disabled:cursor-not-allowed"
            />
            <span className="text-sm text-foreground leading-snug">
              I have read and agree to the Privacy Policy, Terms and Conditions, Cookies Policy,
              and Data Processing Agreement for Meal Manager and Activity Planner.
            </span>
          </label>

          {isAcknowledged && hasReachedBottom && (
            <SignatureReveal name={signatureName} animKey={signatureAnimKey} />
          )}

          {acceptHint && (
            <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200/80 rounded-xl px-3 py-2.5 flex items-center gap-2">
              <Icon name={hasReachedBottom ? 'PenLine' : 'ArrowDown'} size={14} className="shrink-0 text-amber-600" />
              {acceptHint}
            </p>
          )}

          {error && (
            <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-xl px-3 py-2.5">
              {error}
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-3 sm:justify-end pt-1">
            <button
              type="button"
              onClick={onLogout}
              className="px-4 py-2.5 rounded-xl border border-border text-sm font-medium text-foreground hover:bg-surface flex items-center justify-center gap-2 transition-colors"
              disabled={isSubmitting}
            >
              <Icon name="LogOut" size={16} />
              Log Out
            </button>
            <button
              type="button"
              onClick={handleAccept}
              disabled={!canAccept}
              title={acceptHint || undefined}
              className={ACCEPT_BUTTON_CLASS}
            >
              {isSubmitting ? (
                <Icon name="Loader2" size={16} className="animate-spin" />
              ) : (
                <Icon name="CheckCircle" size={16} />
              )}
              Accept and Continue
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TermsAcceptanceModal;
